import {
  Box,
  Modal,
  styled,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import api from "../../api/axiosInstance";
import { Button, FlexRow, Spacer } from "../../components";
import {
  AccountProps,
  ColorsCodeType
} from "../../typings";
import { useSnackbar } from "../../context/SnackbarContext";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 550px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const validationSchema = Yup.object().shape({
  account_title: Yup.string().required("Account Title is required"),
});

interface CreateAccountModalProps {
  open: boolean;
  onClose: () => void;
  account: AccountProps;
}

const CreateAccountModal: React.FC<CreateAccountModalProps> = ({
  open,
  onClose,
  account,
}) => {
  const { showSnackbar } = useSnackbar();
  
  const handleSubmit = async (
    values: AccountProps,
    {
      setSubmitting,
      setErrors,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setErrors: (errors: any) => void;
    }
  ) => {
    try {
      let response;
      
      if (!account?.id) {
        response = await api.post("/accounts/", JSON.stringify(values), {
          headers: {
            "Content-Type": "application/json",
          },
        });
        showSnackbar("Account created successfully!", "success");
      } else {
        response = await api.patch(
          `/accounts/${account.id.toString()}/`,
          JSON.stringify(values),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        showSnackbar("Account updated successfully!", "success");
      }

      // Close the modal on success
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.messages?.[0] || 
                          error?.response?.data?.message || 
                          error?.response?.data?.detail || 
                          `Failed to ${account?.id ? 'update' : 'create'} account. Please try again.`;
      
      showSnackbar(errorMessage, "error");
      
      // Handle validation errors
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const theme = useTheme();
  const {
    palette: { colors },
  } = theme;

  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContent>
        <Typography variant="h6" sx={{ fontWeight: "600" }}>
          {!account?.id ? "Create Account" : "Update Account"}
        </Typography>
        <Spacer vertical={2} />
        <Formik
          initialValues={account}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting }) => (
            <StyledForm>
              <Field
                as={TextField}
                name="account_title"
                label="Account Title"
                fullWidth
                error={touched.account_title && errors.account_title}
                helperText={touched.account_title && errors.account_title}
              />
              <FlexRow justify="flex-end" align="center">
                <Button
                  buttonColor={
                    theme.palette.colors["black"]["600"] as ColorsCodeType
                  }
                  textColor={
                    theme.palette.colors["white"]["400"] as ColorsCodeType
                  }
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Spacer horizontal={1} />

                <Button type="submit" disabled={isSubmitting}>
                  {account?.id ? "Update Account" : "Create"}
                </Button>
              </FlexRow>
            </StyledForm>
          )}
        </Formik>
      </ModalContent>
    </StyledModal>
  );
};

export default CreateAccountModal;
